import { SelectedSubject } from '../types/selected-subject'
import { Subject } from '../types/subject'
import { User } from '../types/user'

export const generateStudentSelectionsReportData = (
  studentsSelections: Array<User & {
    selectedSubjects: SelectedSubject[]
  }>,
  subjects: Subject[]
) => {
  const subjectNumber = studentsSelections[0]?.selectedSubjects?.length ?? 0

  const columns = ['username', 'firstName', 'lastName', ...Array.from({ length: subjectNumber }).map((_, index) => `subject ${index + 1}`)]

  return [
    columns,
    ...studentsSelections.map(s => {
      if (!s) {
        return []
      }
      const subjectNames = s?.selectedSubjects?.map(
        s =>
            `${subjects.find(sub => sub.id === s.subjectId)?.name} - ${
              s.isHighLevel ? 'HL' : 'SL'
            }`
      ) ?? []

      return [s.username, s.firstName, s.lastName, ...subjectNames]
    })
  ]
}

export const generateSubjectSelectionsReportData = (
  studentsSelections: Array<User & {
    selectedSubjects: SelectedSubject[]
  }>,
  subjects: Subject[],
  generateSummaries: boolean
) => {
  const subjectSummary = studentsSelections.reduce<Record<string, any>>((acc, curr) => {
    if (!acc || !curr) {
      return (acc = acc)
    }
    const tempAcc = { ...acc }
    for (const s of curr?.selectedSubjects) {
      if (!tempAcc[s.isHighLevel.toString()]) {
        tempAcc[s.isHighLevel.toString()] = {}
        tempAcc[s.isHighLevel.toString()][s.subjectId.toString()] = 1
      } else if (!tempAcc[s.isHighLevel.toString()][s.subjectId.toString()]) {
        tempAcc[s.isHighLevel.toString()][s.subjectId.toString()] = 1
      } else {
        tempAcc[s.isHighLevel.toString()][s.subjectId.toString()] =
            tempAcc[s.isHighLevel.toString()][s.subjectId.toString()] + 1
      }
    }
    return (acc = tempAcc)
  }, {})

  const indexedSubjectSelectionsHL = Object.keys(subjectSummary.true ?? {}).map(subjectId => ({ index: subjectId, minIndex: subjects.find(s => s.id === Number(subjectId))?.indexes?.reduce((acc, curr) => acc = Math.min(acc, curr.index), 7) })).sort((a, b) => (a?.minIndex ?? 7) > (b?.minIndex ?? 7) ? 1 : -1)
  const indexedSubjectSelectionsSL = Object.keys(subjectSummary.false ?? {}).map(subjectId => ({ index: subjectId, minIndex: subjects.find(s => s.id === Number(subjectId))?.indexes?.reduce((acc, curr) => acc = Math.min(acc, curr.index), 7) })).sort((a, b) => (a?.minIndex ?? 7) > (b?.minIndex ?? 7) ? 1 : -1)

  const columnsHL = indexedSubjectSelectionsHL.map(
    subjectSelection => `${subjects.find(s => s.id === Number(subjectSelection.index))?.name} - HL`
  )
  const columnsSL = indexedSubjectSelectionsSL.map(
    subjectSelection => `${subjects.find(s => s.id === Number(subjectSelection.index))?.name} - SL`
  )

  const valuesHL = indexedSubjectSelectionsHL.map(s =>
    Number(subjectSummary.true[s.index])
  )
  const valuesSL = indexedSubjectSelectionsSL.map(s =>
    Number(subjectSummary.false[s.index])
  )

  const minUsersNumberHL = indexedSubjectSelectionsHL.map(
    sub => subjects.find(s => s.id === Number(sub.index))?.minHLUsersNumber
  )

  const minUsersNumberSL = indexedSubjectSelectionsSL.map(
    sub => subjects.find(s => s.id === Number(sub.index))?.minSLUsersNumber
  )

  const percentageHL = valuesHL.map((v, index) => (v * 100 / (minUsersNumberHL[index]! || 1)).toFixed(2) + ' %')
  const percentageSL = valuesSL.map((v, index) => (v * 100 / (minUsersNumberSL[index]! || 1)).toFixed(2) + ' %')

  const groups = valuesSL.concat(valuesHL).map(v => Math.ceil(v / 18))

  return generateSummaries
    ? [['', ...columnsSL.concat(columnsHL)], ['current groups filling', ...valuesSL.concat(valuesHL)], ['minimal users number', ...minUsersNumberSL.concat(minUsersNumberHL)], ['% subject filling', ...percentageSL.concat(percentageHL)], ['potential groups number', ...groups]]
    : [['', ...columnsSL.concat(columnsHL)], ['current groups filling', ...valuesSL.concat(valuesHL)]]
}
